<script setup lang="ts">
  import { shallowRef, useTemplateRef, watch } from 'vue'
  import { Button, Tabs, TabsList, TabsTrigger } from '@rapoo/element'
  import KeyboardPanel from './components/KeyboardPanel.vue'
  import ArcGaugeSlider from './components/ArcGaugeSlider.vue'
  import ItineraryTrigger from './components/ItineraryTrigger.vue'
  import DeadZone from './components/DeadZone.vue'
  import RTSensitivityTrigger from './components/RTSensitivityTrigger.vue'
  import CalibrationPanel from './components/CalibrationPanel.vue'
  import TabShaft from './components/TabShaft.vue'
  import { useItinerarySetting } from './composables/useItinerarySetting'
  import { useShaftType } from './composables/useShaftType'

  type PageMode = 'calibration' | 'demo'
  type ActiveTab = 'travel' | 'shaft'

  const tabConfigs: Array<{ label: string; value: ActiveTab }> = [
    { label: 'itinerary.tabs.travel', value: 'travel' },
    { label: 'itinerary.tabs.shaft', value: 'shaft' },
  ]

  const currentMode = shallowRef<PageMode>('demo')
  const activeTabValue = shallowRef<ActiveTab>('travel')
  const keyboardPanelRef = useTemplateRef<InstanceType<typeof KeyboardPanel>>('keyboardPanel')
  const calibrationCounts = shallowRef(new Map<string, number>())

  const {
    keyTravelMap,
    editingState,
    isInitializing,
    canEditTrigger,
    triggerSliderDisabled,
    rtSwitchDisabled,
    deadZoneEnabled,
    rtTriggerEnable,
    continuousTriggerEnable,
    rtPrecisionEnabled,
    rtSensitivityEnabled,
    rtPrecisionIndex,
    rtPrecisionStep,
    rtPrecisionLabels,
    isRelated,
    getKeyTravel,
    handleSelectionChange: handleItinerarySelectionChange,
    handleTravelChange,
    handleRtTriggerEnableChange,
    handleContinuousTriggerEnableChange,
    handleReleaseDeadzoneChange,
    handlePressDeadzoneChange,
    handlePressRtChange,
    handleReleaseRtChange,
    handleRelatedChange,
    handleRtPrecisionChange,
  } = useItinerarySetting()
  const {
    shaftTypeMap,
    selectedShaftType,
    canSaveShaftType,
    loadKeyShaftTypes,
    handleSelectionChange: handleShaftTypeSelectionChange,
    selectShaftType,
    saveSelectedShaftTypes,
  } = useShaftType()

  const getDemoKeyTravel = (index: number) => (index < 0 ? editingState : getKeyTravel(index))

  const handleKeySelectionChange = (keys: Array<{ index: number }>) => {
    handleItinerarySelectionChange(keys)
    handleShaftTypeSelectionChange(keys)
  }

  const handleSaveShaftTypes = async () => {
    if (await saveSelectedShaftTypes()) keyboardPanelRef.value?.clearSelection()
  }

  const handleChangeMode = (mode: PageMode) => {
    if (currentMode.value === 'calibration' && mode === 'demo') calibrationCounts.value = new Map()
    currentMode.value = mode
  }

  const handleChangeTab = (tab: ActiveTab) => {
    if (currentMode.value === 'calibration') calibrationCounts.value = new Map()
    activeTabValue.value = tab
    currentMode.value = 'demo'
  }

  const handleCalibrationCountsChange = (counts: Map<string, number>) => {
    calibrationCounts.value = counts
  }

  watch(activeTabValue, (tab) => {
    if (tab === 'shaft') void loadKeyShaftTypes()
  })
</script>

<template>
  <div
    v-loading="isInitializing"
    class="max-w-360 m-auto w-full h-full flex flex-col justify-center items-center"
  >
    <div class="flex-1 flex justify-center items-center">
      <KeyboardPanel
        ref="keyboardPanel"
        :mode="currentMode"
        :is-shaft-tab="activeTabValue === 'shaft'"
        :key-travel-map="keyTravelMap"
        :shaft-type-map="shaftTypeMap"
        :calibration-counts="calibrationCounts"
        @selection-change="handleKeySelectionChange"
      />
    </div>

    <Tabs
      v-model="activeTabValue"
      class="flex flex-col items-center w-full gap-4.5 h-82 shrink-0 mb-2"
    >
      <TabsList class="p-0 flex items-center gap-4 h-10">
        <TabsTrigger v-for="tab in tabConfigs" :key="tab.value" :value="tab.value">
          <Button
            color="glass"
            :active="activeTabValue === tab.value"
            class="h-10"
            @click="handleChangeTab(tab.value)"
          >
            {{ $t(tab.label) }}
          </Button>
        </TabsTrigger>
      </TabsList>

      <div
        v-if="activeTabValue === 'travel' && currentMode === 'demo'"
        class="w-full h-67.5 min-h-fit overflow-x-auto flex items-start justify-center gap-2"
      >
        <ArcGaugeSlider :get-key-travel="getDemoKeyTravel" @update:mode="handleChangeMode" />

        <div class="flex gap-2 glass-frosted relative rounded-8xl w-250 min-h-67.5 p-4">
          <ItineraryTrigger
            :absolute-trigger="editingState.travel"
            :rt-trigger-enable="rtTriggerEnable"
            :continuous-trigger-enable="continuousTriggerEnable"
            :rt-switch-disabled="rtSwitchDisabled"
            :can-edit-trigger="canEditTrigger"
            :trigger-slider-disabled="triggerSliderDisabled"
            @update:absolute-trigger="handleTravelChange"
            @update:rt-trigger-enable="handleRtTriggerEnableChange"
            @update:continuous-trigger-enable="handleContinuousTriggerEnableChange"
          />

          <DeadZone
            :top-dead-zone="editingState.pressDeadzone"
            :bottom-dead-zone="editingState.releaseDeadzone"
            :dead-zone-enabled="deadZoneEnabled"
            @update:top-dead-zone="handlePressDeadzoneChange"
            @update:bottom-dead-zone="handleReleaseDeadzoneChange"
          />

          <RTSensitivityTrigger
            :is-related="isRelated"
            :rt-trigger-sensitivity="editingState.pressRt"
            :rt-reset-sensitivity="editingState.releaseRt"
            :sensitivity-enabled="rtSensitivityEnabled"
            :related-toggle-enabled="rtSensitivityEnabled"
            :precision-enabled="rtPrecisionEnabled"
            :rt-precision-index="rtPrecisionIndex"
            :precision-step="rtPrecisionStep"
            :precision-labels="rtPrecisionLabels"
            @update:is-related="handleRelatedChange"
            @update:rt-trigger-sensitivity="handlePressRtChange"
            @update:rt-reset-sensitivity="handleReleaseRtChange"
            @change-precision="handleRtPrecisionChange"
          />
        </div>
      </div>

      <div
        v-if="activeTabValue === 'travel' && currentMode === 'calibration'"
        class="w-full h-67.5 overflow-x-auto"
      >
        <CalibrationPanel
          @update:mode="handleChangeMode"
          @calibration-counts-change="handleCalibrationCountsChange"
        />
      </div>

      <TabShaft
        v-if="activeTabValue === 'shaft'"
        :selected-shaft-type="selectedShaftType"
        :save-disabled="!canSaveShaftType"
        @select="selectShaftType"
        @save="handleSaveShaftTypes"
      />
    </Tabs>
  </div>
</template>

<style lang="scss" scoped></style>
